import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import {
  eWayBillCancelAction,
  eWayBillMastersAction,
} from "../../../redux/actions/sales-action";
import errorToast from "../../helper-components/error-toast";
import successToast from "../../helper-components/success-toast";
import CancelDocumentModal from "../cancel-document-modal";
import { parseIrpError } from "../e-invoice/create-e-invoice";

/* NIC e-way bill cancellation reason codes – used only if the masters API returns none */
const DEFAULT_CANCEL_REASONS = [
  { value: "1", label: "Duplicate" },
  { value: "2", label: "Order Cancelled" },
  { value: "3", label: "Data Entry mistake" },
  { value: "4", label: "Others" },
];

/* NIC limit for the e-way bill cancel remark */
const REMARK_MAX_LENGTH = 50;

/*
 * Popup to cancel an e-way bill from the e-Way Bills list.
 * soitId / ewbNo identify the bill; transactionNo / cancelUntil are shown for context.
 */
const CancelEWayBillModal = ({ soitId, ewbNo, transactionNo, cancelUntil, onClose, onCancelled }) => {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(true);

  useEffect(() => {
    dispatch(
      eWayBillMastersAction(
        (res) => {
          const m = res?.data ?? res ?? {};
          const options = (m.cancellationReason ?? [])
            .map((r) => ({ value: String(r?.value ?? r?.code ?? ""), label: r?.name ?? r?.label ?? "" }))
            .filter((o) => o.value);
          setReasons(options.length ? options : DEFAULT_CANCEL_REASONS);
          setReasonsLoading(false);
        },
        () => {
          setReasons(DEFAULT_CANCEL_REASONS);
          setReasonsLoading(false);
        }
      )
    );
  }, []);

  const onFailed = (err) => {
    setSaving(false);
    const parsed = parseIrpError(err);
    setError(parsed);
    errorToast(parsed.details[0] ?? parsed.message ?? "Failed to cancel e-Way Bill");
  };

  const handleConfirm = ({ reason, remark }) => {
    setSaving(true);
    setError(null);
    dispatch(
      eWayBillCancelAction(
        {
          cancelItems: [
            {
              soit_id: soitId,
              eWayBillNo: String(ewbNo),
              CanReasonCode: reason,
              CanRemark: remark,
            },
          ],
        },
        (res) => {
          /* the bulk API can answer 200 with status false for a failed item */
          if (res?.status === false) {
            onFailed(res);
            return;
          }
          setSaving(false);
          successToast(res?.message ?? "e-Way Bill cancelled successfully");
          onCancelled?.();
        },
        onFailed
      )
    );
  };

  return (
    <CancelDocumentModal
      title="Cancel e-Way Bill"
      infoLines={[
        { label: "Transaction #", value: transactionNo },
        { label: "EWB #", value: ewbNo },
      ]}
      note={
        cancelUntil && moment(cancelUntil).isValid()
          ? `Can be cancelled until ${moment(cancelUntil).format("MMM D, YYYY, hh:mm A")}`
          : null
      }
      reasons={reasons}
      reasonsLoading={reasonsLoading}
      remarkMaxLength={REMARK_MAX_LENGTH}
      saving={saving}
      error={error}
      onClose={onClose}
      onConfirm={handleConfirm}
    />
  );
};

export default CancelEWayBillModal;
